import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { simulateAnalysis } from '../services/staticAnalysis';
import Header from '../components/Header';
import '../App.css';

const SeguridadNacional2025 = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [modalContent, setModalContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Datos de la estrategia
  const strategyData = {
    regions: [
      { name: 'China', threat: 95, priority: 'Máxima', color: '#b91c1c' },
      { name: 'Rusia', threat: 85, priority: 'Alta', color: '#dc2626' },
      { name: 'Irán', threat: 75, priority: 'Alta', color: '#ea580c' },
      { name: 'Corea del Norte', threat: 70, priority: 'Media-Alta', color: '#f59e0b' },
      { name: 'Europa', threat: 30, priority: 'Cooperación', color: '#10b981' }
    ],
    priorities: [
      { 
        id: 'migration_end', 
        title: 'Fin Migración Masiva', 
        icon: '🛑', 
        summary: 'La era de fronteras abiertas ha terminado.',
        detail: 'Seguridad fronteriza como pilar de seguridad nacional. Protección contra invasión, terrorismo y tráfico humano. Un país sin fronteras no es un país.'
      },
      { 
        id: 'rights', 
        title: 'Derechos y Libertades', 
        icon: '📜', 
        summary: 'Protección de derechos naturales dados por Dios.',
        detail: 'Libertad de expresión, religión y conciencia son sagradas. Oposición a restricciones antidemocráticas y censura, tanto en casa como entre aliados.'
      },
      { 
        id: 'burden_sharing', 
        title: 'Reparto de Cargas', 
        icon: '💰', 
        summary: 'Red de aliados que asumen responsabilidad.',
        detail: 'Implementación del "Compromiso de La Haya": aliados de la OTAN deben gastar el 5% del PIB. EE. UU. convoca y apoya, pero no carga con todo el peso.'
      },
      { 
        id: 'realignment', 
        title: 'Realineamiento por Paz', 
        icon: '🕊️', 
        summary: 'Diplomacia presidencial para cerrar conflictos.',
        detail: 'Uso de diplomacia no convencional para resolver conflictos estancados (ej. Balcanes, Cáucaso), realineando regiones hacia la estabilidad y abriendo mercados.'
      },
      { 
        id: 'economic_sec', 
        title: 'Seguridad Económica', 
        icon: '💼', 
        summary: 'Comercio balanceado y reindustrialización.',
        detail: 'Reducción de déficits comerciales, asegurar cadenas de suministro críticas (minerales), y re-shoring de industrias vitales. La economía es seguridad nacional.'
      }
    ]
  };

  const insights = [
    { 
      type: 'thesis', 
      title: 'Monroe Recargado', 
      icon: '🌎', 
      text: 'América Latina vuelve a ser el "patio trasero". El Corolario Trump es claro: China, ni te acerques. El hemisferio cerrado por inventario.',
      context: 'La Doctrina Monroe (1823) advertía a potencias europeas no intervenir en América. Esta actualización busca explícitamente expulsar la influencia económica y militar moderna de China del hemisferio occidental.'
    },
    { 
      type: 'thesis', 
      title: 'Realismo "A la Carta"', 
      icon: '🤝', 
      text: '¿Derechos humanos? Solo si nos caes bien. Un pragmatismo cínico donde cualquier dictador es amigo si compra "Made in USA" y odia a nuestros enemigos.',
      context: 'Ruptura con el "idealismo wilsoniano" o neoconservador que buscaba exportar democracia. Se prioriza la estabilidad y el interés comercial sobre los valores políticos internos de los socios.'
    },
    { 
      type: 'thesis', 
      title: 'Mercantilismo de Guerra', 
      icon: '🏭', 
      text: 'Adam Smith se revuelca en su tumba. La economía ahora es un arma de seguridad nacional; el proteccionismo se disfraza de patriotismo.',
      context: 'Rechazo al libre mercado puro en favor de una política industrial dirigida por el Estado (aranceles, subsidios), similar a modelos asiáticos pero con enfoque nacionalista americano.'
    }
  ];

  const handleAnalyzeInsight = async (insight) => {
    setIsLoading(true);
    setIsModalOpen(true);
    setModalContent('Analizando con IA...');

    try {
      const systemPrompt = `Actúa como un analista político crítico e imparcial. Analiza el siguien
te punto extraído de la Estrategia de Seguridad Nacional 2025 de EE. UU.
        Titular irónico del tema: "${insight.title}"
        Descripción crítica: "${insight.text}"
        Contexto histórico/político: "${insight.context}"
        Instrucciones: Proporciona un análisis de 3 puntos sobre qué significa realmente esto. 1. ¿Qué hay detrás de la retórica? 2. Riesgos o contradicciones. 3. Efecto en el orden mundial. Mantén un tono profesional pero escéptico. Usa Markdown.`;

      const userQuery = `Analiza: ${insight.title}`;
      const response = await simulateAnalysis("security", insight.title);
      setModalContent(response);
    } catch (error) {
      console.error('Error analyzing insight:', error);
      setModalContent(`**Error de Análisis:** No se pudo conectar con el sistema de inteligencia.\n\n*${error.message || "Verifique la clave API."}*`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Resumen Ejecutivo */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: '1rem',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        padding: '2rem'
      }}>
        <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem', marginBottom: '1rem', borderBottom: '2px solid #fbbf24', paddingBottom: '0.5rem' }}>
          Resumen Ejecutivo: Restaurando la Fuerza Americana
        </h3>
        
        <div style={{ color: '#cbd5e1', lineHeight: '1.8', fontSize: '1.1rem' }}>
          <p style={{ marginBottom: '1rem' }}>
            <strong>I. Visión General:</strong> La Estrategia de Seguridad Nacional de 2025 establece una hoja de ruta urgente para revertir el declive nacional y restaurar la preeminencia de Estados Unidos. Basada en el principio de <em>"Paz a través de la Fuerza"</em>, la estrategia rechaza el globalismo y el intervencionismo fallido en favor de un realismo flexible que prioriza inequívocamente los intereses americanos.
          </p>
          
          <p style={{ marginBottom: '1rem' }}>
            <strong>II. Intereses Nacionales Vitales:</strong> El documento define la supervivencia de la república como el interés supremo. Esto requiere el control total de las fronteras para detener la migración masiva, la protección de la soberanía frente a instituciones transnacionales, y el mantenimiento de una superioridad militar incuestionable, incluyendo la construcción de una "Cúpula Dorada" de defensa antimisiles.
          </p>
          
          <p style={{ marginBottom: '1rem' }}>
            <strong>III. Seguridad Económica:</strong> Se establece que la seguridad económica es seguridad nacional. El plan exige una reindustrialización masiva ("re-shoring"), el fin de la dependencia de cadenas de suministro extranjeras (especialmente de China) y el dominio energético total mediante la expansión de combustibles fósiles y energía nuclear.
          </p>
        </div>
      </div>

      {/* Prioridades Estratégicas */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: '1rem',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        padding: '2rem'
      }}>
        <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          Prioridades Estratégicas
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          {strategyData.priorities.map((priority, index) => (
            <div key={index} style={{
              background: 'rgba(15, 23, 42, 0.8)',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.1)',
              padding: '1.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#fbbf24';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '2rem', marginRight: '1rem' }}>{priority.icon}</span>
                <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {priority.title}
                </h4>
              </div>
              <p style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>{priority.summary}</p>
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>{priority.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalysis = () => (
    <div className="space-y-6">
      <div style={{
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: '1rem',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        padding: '2rem'
      }}>
        <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          Análisis Crítico con IA
        </h3>
        <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>
          Haz click en cualquier punto para obtener un análisis crítico generado por IA:
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
          {insights.map((insight, index) => (
            <div key={index} 
              onClick={() => handleAnalyzeInsight(insight)}
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(148, 163, 184, 0.1)',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '2rem', marginRight: '1rem' }}>{insight.icon}</span>
                <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {insight.title}
                </h4>
              </div>
              
              <p style={{ color: '#cbd5e1', marginBottom: '1rem', fontStyle: 'italic' }}>
                "{insight.text}"
              </p>
              
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                {insight.context}
              </p>
              
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.5rem 1rem', 
                background: 'rgba(59, 130, 246, 0.2)', 
                borderRadius: '0.25rem',
                textAlign: 'center'
              }}>
                <span style={{ color: '#93c5fd', fontSize: '0.9rem' }}>
                  🤖 Click para análisis con IA
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderThreatMatrix = () => (
    <div style={{
      background: 'rgba(30, 41, 59, 0.8)',
      borderRadius: '1rem',
      border: '1px solid rgba(148, 163, 184, 0.2)',
      padding: '2rem'
    }}>
      <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
        Matriz de Amenazas Regionales
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
        {strategyData.regions.map((region, index) => (
          <div key={index} style={{
            background: 'rgba(15, 23, 42, 0.8)',
            borderRadius: '0.5rem',
            border: '1px solid rgba(148, 163, 184, 0.1)',
            padding: '1.5rem'
          }}>
            <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', marginBottom: '1rem' }}>
              {region.name}
            </h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: '#94a3b8' }}>Nivel de Amenaza</span>
                <span style={{ color: '#f1f5f9', fontWeight: 'bold' }}>{region.threat}%</span>
              </div>
              
              <div style={{
                width: '100%',
                height: '8px',
                background: 'rgba(148, 163, 184, 0.2)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${region.threat}%`,
                  height: '100%',
                  background: region.color,
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
            
            <div style={{
              padding: '0.5rem 1rem',
              background: `${region.color}20`,
              borderRadius: '0.25rem',
              textAlign: 'center'
            }}>
              <span style={{ color: region.color, fontWeight: 'bold' }}>
                {region.priority}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      <Header />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <Link to="/">
            <button style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}>
              ← Volver al Inicio
            </button>
          </Link>
        </div>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ 
            marginBottom: '0.5rem', 
            color: '#fbbf24', 
            fontWeight: 'bold', 
            letterSpacing: '0.1em', 
            textTransform: 'uppercase', 
            fontSize: '0.875rem' 
          }}>
            La Casa Blanca - Washington
          </div>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            marginBottom: '0.5rem',
            color: '#f1f5f9',
            fontFamily: 'serif'
          }}>
            Estrategia de Seguridad Nacional
          </h1>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '300', 
            fontStyle: 'italic', 
            opacity: 0.9,
            color: '#cbd5e1'
          }}>
            Estados Unidos de América - Noviembre 2025
          </h2>
          <p style={{ 
            marginTop: '1rem', 
            fontSize: '1.125rem', 
            fontWeight: '600', 
            color: '#94a3b8' 
          }}>
            "Paz a través de la Fuerza"
          </p>
        </div>

        {/* Navigation Tabs */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {[
            { id: 'overview', label: 'Resumen Ejecutivo', icon: '📋' },
            { id: 'analysis', label: 'Análisis Crítico', icon: '🤖' },
            { id: 'threats', label: 'Matriz de Amenazas', icon: '🎯' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                  : 'rgba(30, 41, 59, 0.8)',
                color: activeTab === tab.id ? '#0f172a' : '#cbd5e1',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                transition: 'all 0.3s ease',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'analysis' && renderAnalysis()}
          {activeTab === 'threats' && renderThreatMatrix()}
        </div>

        {/* Navigation */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginTop: '3rem', 
          padding: '2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <Link to="/">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ← Volver al Dashboard
            </button>
          </Link>
          
          <Link to="/seguridad-nacional-analisis">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              color: '#0f172a',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              Análisis Crítico ➡️
            </button>
          </Link>
        </div>
      </div>

      {/* Modal para análisis IA */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem' }}>
                🤖 Análisis con IA
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              {isLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🤖</div>
                  <p>Analizando con IA...</p>
                </div>
              ) : (
                <div 
                  style={{ 
                    color: '#cbd5e1',
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: modalContent
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #f1f5f9;">$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      .replace(/\n\n/g, '</p><p style="margin: 1rem 0;">')
                      .replace(/^/, '<p style="margin: 1rem 0;">')
                      .replace(/$/, '</p>')
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeguridadNacional2025;